import client from "../graphqlClient";
import { httpGet } from "../Http";
import { getGraphqlResult } from "../../Utilities/Functions";
import { saveAs } from "file-saver";
import {
  SEARCH_RECORDS_LOCALLY,
  SEARCH_RECORDS_FROM_MDM,
  GET_ENTITY_ID_FROM_MDM_ID,
  GET_ENTITY_ID_FROM_DUNS_NUMBER,
  GET_ENTITY,
  GET_ASSOCIATIONS,
  UPDATE_ASSOCIATIONS,
  UPDATE_ASSOCIATION_ATTRIBUTE,
  UPDATE_ENTITY_ATTRIBUTES,
  CREATE_ENTITY,
  GET_DOCUMENT_TYPES,
  GET_ENTITY_DOCUMENTS,
  FINALIZE_SCORE,
  UPDATE_CONFIDENTIALITY,
  REQUEST_BLOCK,
  REQUEST_UNBLOCK,
  CREATE_ENTITY_CONTACT,
  UPDATE_ENTITY_CONTACT,
  SEARCH_RECORDS_FROM_DNB,
  GET_RELATED_ENTITIES,
  CREATE_RELATED_ENTITY,
  DELETE_RELATED_ENTITY,
  GET_ENTITY_LINKS,
  CREATE_ENTITY_LINK,
  UPDATE_ENTITY_LINK,
  UPDATE_ENTITY_DOCUMENT_NAME,
  REMOVE_ENTITY_LINK,
  REMOVE_ENTITY_CONTACT,
  REMOVE_ENTITY_DOCUMENT,
  DELETE_ENTITY_ASSOCIATION,
  DELETE_ENTITY_ASSOCIATIONS,
  SEND_ASSOCIATIONS_TO_WC1,
  GET_CHECK_VALIDITY_PERIOD,
  GET_ENTITY_COMMENT_HISTORY,
  GET_FINALIZE_COMMENT,
  GET_DATA_AUGMENTATION,
  GET_ENTITY_ORG_STRUCTURE,
  IMPORT_EXTERNAL_DOCUMENTS,
  GET_DATA_AUGMENTATION_SOURCES,
} from "./graphql";

const getEntity = async (params) => {
  const { entityId } = params;
  const response = await client.query({
    query: GET_ENTITY,
    variables: {
      entityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getEntity");
  return results;
};

const getParentsRecords = async (params) => {
  const { entityId, entityCategory } = params;
  const response = await client.query({
    query: GET_ENTITY_ORG_STRUCTURE,
    variables: {
      entityId,
      entityCategory,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getParentsRecords");
  return results;
};

const searchLocalRecords = async (params) => {
  const { pageNumber, rowsPerPage, searchCriteria } = params;
  const response = await client.query({
    query: SEARCH_RECORDS_LOCALLY,
    variables: {
      pageNumber,
      rowsPerPage,
      searchCriteria,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "searchLocalEntities");
  return results;
};

const searchMdmRecords = async (params) => {
  const { pageNumber, rowsPerPage, searchCriteria } = params;
  const { companyName, dunsNumber, city, countryCode, state, postCode } =
    searchCriteria || {};
  const response = await client.query({
    query: SEARCH_RECORDS_FROM_MDM,
    variables: {
      name: companyName,
      dunsNumber: Number(dunsNumber),
      city,
      countryCode,
      state,
      postCode,
      pageNumber,
      rowsPerPage,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "searchMDMEntity");
  return results;
};

const searchDnbRecords = async (params) => {
  const { searchCriteria, pageNumber, rowsPerPage } = params;
  const { companyName, dunsNumber, city, countryCode, state, postCode } =
    searchCriteria || {};
  const response = await client.query({
    query: SEARCH_RECORDS_FROM_DNB,
    variables: {
      name: companyName,
      dunsNumber: Number(dunsNumber),
      city,
      countryCode,
      state,
      postCode,
      pageNumber,
      rowsPerPage,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "searchDNBEntity");
  return results;
};

const getEntityIdFromMdmId = async (mdmId) => {
  const response = await client.query({
    query: GET_ENTITY_ID_FROM_MDM_ID,
    variables: {
      mdmId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getEntityIdFromMdmId");
  return results;
};

const getValidityPeriod = async (entityId, checkType, proceedAtRisk) => {
  const response = await client.query({
    query: GET_CHECK_VALIDITY_PERIOD,
    variables: {
      entityId,
      checkType,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getValidityPeriod");
  return results;
};

const getEntityIdFromDunsNumber = async (dunsNumber) => {
  const response = await client.query({
    query: GET_ENTITY_ID_FROM_DUNS_NUMBER,
    variables: {
      dunsNumber,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getEntityIdFromDunsNumber");
  return results;
};

const getAssociations = async (entityId, entityCategory, entitySubcategory) => {
  const response = await client.query({
    query: GET_ASSOCIATIONS,
    variables: {
      entityId,
      entityCategory,
      entitySubcategory,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getAssociations");
  return results;
};

const updateAssociations = async (params) => {
  const {
    sourceEntityId,
    targetEntityId,
    targetMdmId,
    targetDnbId,
    associations,
    additionalInformation,
    action,
  } = params;
  const response = await client.query({
    query: UPDATE_ASSOCIATIONS,
    variables: {
      sourceEntityId,
      targetEntityId,
      targetMdmId,
      targetDnbId,
      associations,
      additionalInformation,
      action: action || "",
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateAssociations");
  return results;
};

const deleteEntityAssociation = async (params) => {
  const { entityId, targetEntityId, associationId } = params;

  const response = await client.query({
    query: DELETE_ENTITY_ASSOCIATION,
    variables: {
      entityId,
      targetEntityId,
      associationId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeEntityAssociation");
  return results;
};

const deleteEntityAssociations = async (associations) => {
  const response = await client.query({
    query: DELETE_ENTITY_ASSOCIATIONS,
    variables: { associations },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeEntityAssociations");
  return results;
};

const updateAssociationAttribute = async (params) => {
  const {
    sourceEntityId,
    targetEntityId,
    associationId,
    attributeId,
    dataType,
    value,
  } = params;
  const response = await client.query({
    query: UPDATE_ASSOCIATION_ATTRIBUTE,
    variables: {
      sourceEntityId,
      targetEntityId,
      associationId,
      attributeId,
      dataType,
      value,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(
    response,
    "updateAssociationAttribute",
  );
  return results;
};

const updateEntity = async (params) => {
  const { entityId, attributes, action } = params;
  const response = await client.query({
    query: UPDATE_ENTITY_ATTRIBUTES,
    variables: {
      entityId,
      attributes,
      action: action || "",
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateEntity");
  return results;
};

const sendAssociationsToWc1 = async (params) => {
  const { entityId } = params;

  const response = await client.query({
    query: SEND_ASSOCIATIONS_TO_WC1,
    variables: {
      entityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "sendAssociationsToWc1");
  return results;
};

const createNewEntity = async (params) => {
  const {
    entityName,
    entityCategory,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    city,
    county,
    stateProvince,
    region,
    postCode,
    countryId,
    jllCountryId,
    checkType,
  } = params;

  const response = await client.query({
    query: CREATE_ENTITY,
    variables: {
      entityName,
      entityCategory,
      addressLine1,
      addressLine2,
      addressLine3,
      addressLine4,
      city,
      county,
      stateProvince,
      region,
      postCode,
      countryId,
      jllCountryId,
      checkType,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "createEntity");
  return results;
};

const addEntityLink = async (params) => {
  const { entityId, url, description, type } = params;

  const response = await client.query({
    query: CREATE_ENTITY_LINK,
    variables: {
      entityId,
      url,
      description,
      type,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "addEntityLink");
  return results;
};

const removeEntityLinks = async (id, entityId, type) => {
  const response = await client.query({
    query: REMOVE_ENTITY_LINK,
    variables: {
      id,
      entityId,
      type,
    },

    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeEntityLink");
  return results;
};

const removeEntityDocument = async (documentId, entityId) => {
  const response = await client.query({
    query: REMOVE_ENTITY_DOCUMENT,
    variables: {
      documentId,
      entityId,
    },

    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeEntityDocument");
  return results;
};

const removeEntityContact = async (id, entityId) => {
  const response = await client.query({
    query: REMOVE_ENTITY_CONTACT,
    variables: {
      id,
      entityId,
    },

    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeEntityContact");
  return results;
};

const updateEntityLink = async (params) => {
  const { id, url, description, entityId, type } = params;

  const response = await client.query({
    query: UPDATE_ENTITY_LINK,
    variables: {
      id,
      url,
      description,
      entityId,
      type,
    },

    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateEntityLink");
  return results;
};

const updateEntityDocumentName = async (params) => {
  const { entityId, documentId, category, documentName } = params;

  const response = await client.query({
    query: UPDATE_ENTITY_DOCUMENT_NAME,
    variables: {
      entityId,
      documentId,
      category,
      documentName,
    },

    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateEntityDocumentName");
  return results;
};

const addEntityContact = async (params) => {
  const {
    entityId,
    addressType,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    town,
    county,
    region,
    postCode,
    countryId,
    action,
  } = params;

  const response = await client.query({
    query: CREATE_ENTITY_CONTACT,
    variables: {
      entityId,
      addressType,
      addressLine1,
      addressLine2,
      addressLine3,
      addressLine4,
      town,
      county,
      region,
      postCode,
      countryId,
      action: action || "",
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "addEntityContact");
  return results;
};

const updateEntityContact = async (params) => {
  const {
    id,
    entityId,
    addressType,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    town,
    county,
    region,
    postCode,
    countryId,
  } = params;

  const response = await client.query({
    query: UPDATE_ENTITY_CONTACT,
    variables: {
      id,
      entityId,
      addressType,
      addressLine1,
      addressLine2,
      addressLine3,
      addressLine4,
      town,
      county,
      region,
      postCode,
      countryId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateEntityContact");
  return results;
};

const getDocumentTypes = async (section) => {
  const response = await client.query({
    query: GET_DOCUMENT_TYPES,
    variables: { section },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getDocumentTypes");
  return results;
};

const getEntityDocuments = async (entityId, category) => {
  const response = await client.query({
    query: GET_ENTITY_DOCUMENTS,
    variables: {
      entityId,
      category,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getEntityDocuments");
  return results;
};

const getEntityLinks = async (params) => {
  const { entityId, type } = params;
  const response = await client.query({
    query: GET_ENTITY_LINKS,
    variables: {
      entityId,
      type,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getEntityLinks");
  return results;
};

const getCommentHistory = async (entityId) => {
  const response = await client.query({
    query: GET_ENTITY_COMMENT_HISTORY,
    variables: {
      entityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getCommentHistory");
  return results;
};

const getUserFinalizeComment = async (scoreId) => {
  const response = await client.query({
    query: GET_FINALIZE_COMMENT,
    variables: {
      scoreId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getUserFinalizeComment");
  return results;
};

const finalizeScore = async (params) => {
  const {
    entityId,
    checkType,
    acceptedScore,
    guidedScore,
    userComment,
    userValidFrom,
    userValidTo,
    proceedAtRisk,
  } = params;

  const response = await client.query({
    query: FINALIZE_SCORE,
    variables: {
      entityId,
      checkType,
      acceptedScore,
      guidedScore,
      userComment,
      userValidFrom,
      userValidTo,
      proceedAtRisk,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "finalizeScore");
  return results;
};

const updateConfidentiality = async (params) => {
  const { entityId, confidential } = params;

  const response = await client.query({
    query: UPDATE_CONFIDENTIALITY,
    variables: {
      entityId,
      confidential,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "updateConfidentiality");
  return results;
};

const requestBlock = async (params) => {
  const { entityId, reason } = params;

  const response = await client.query({
    query: REQUEST_BLOCK,
    variables: {
      entityId,
      reason,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "blockRequest");
  return results;
};

const requestUnblock = async (params) => {
  const { entityId, reason } = params;

  const response = await client.query({
    query: REQUEST_UNBLOCK,
    variables: {
      entityId,
      reason,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "unblockRequest");
  return results;
};

const exportEntity = async (entityId, exportType) => {
  const response = await httpGet(
    `/reports/export/${entityId}`,
    {
      type: exportType,
    },
    true,
  );

  const fileName = response.headers
    .get("content-disposition")
    .split("=")[1]
    .split('"')[1];
  const fileData = new Blob([response.data], {
    type: response.headers.get("content-type"),
  });
  saveAs(fileData, fileName);
};

const getRelatedEntities = async (entityId) => {
  const response = await client.query({
    query: GET_RELATED_ENTITIES,
    variables: {
      entityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getRelatedEntities");
  return results;
};

const createRelatedEntity = async (params) => {
  const {
    entityId,
    relationType,
    relatedLocalEntityId,
    relatedMdmId,
    relatedDnbId,
    entityName,
    entityCategory,
    addressLine1,
    addressLine2,
    addressLine3,
    addressLine4,
    city,
    county,
    stateProvince,
    region,
    postCode,
    countryId,
  } = params;

  const response = await client.query({
    query: CREATE_RELATED_ENTITY,
    variables: {
      entityId,
      relationType,
      relatedLocalEntityId,
      relatedMdmId,
      relatedDnbId,
      entityName,
      entityCategory,
      addressLine1,
      addressLine2,
      addressLine3,
      addressLine4,
      city,
      county,
      stateProvince,
      region,
      postCode,
      countryId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "createRelatedEntity");
  return results;
};

const removeRelatedEntity = async (params) => {
  const { entityId, relatedEntityId } = params;

  const response = await client.query({
    query: DELETE_RELATED_ENTITY,
    variables: {
      entityId,
      relatedEntityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "removeRelatedEntity");
  return results;
};

const getDataAugmentation = async (
  entityId,
  sources,
  action,
  pagination = [],
) => {
  const response = await client.query({
    query: GET_DATA_AUGMENTATION,
    variables: {
      entityId,
      sources,
      action,
      pagination,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getAugmentationData");
  return results;
};

const importExternalDocuments = async (params) => {
  const { entityId, documents } = params;
  const response = await client.query({
    query: IMPORT_EXTERNAL_DOCUMENTS,
    variables: {
      entityId,
      documents,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "importExternalDocuments");
  return results;
};

const getAugmentationSources = async (entityId) => {
  const response = await client.query({
    query: GET_DATA_AUGMENTATION_SOURCES,
    variables: {
      entityId,
    },
    fetchPolicy: "no-cache",
  });
  const results = await getGraphqlResult(response, "getAugmentationSources");
  return results;
};

export {
  searchLocalRecords,
  searchMdmRecords,
  getEntityIdFromMdmId,
  getEntityIdFromDunsNumber,
  getEntity,
  getAssociations,
  updateAssociations,
  updateAssociationAttribute,
  updateEntity,
  createNewEntity,
  getDocumentTypes,
  getEntityDocuments,
  finalizeScore,
  updateConfidentiality,
  requestBlock,
  requestUnblock,
  addEntityContact,
  updateEntityContact,
  exportEntity,
  searchDnbRecords,
  getRelatedEntities,
  createRelatedEntity,
  removeRelatedEntity,
  getEntityLinks,
  addEntityLink,
  updateEntityLink,
  updateEntityDocumentName,
  removeEntityLinks,
  removeEntityContact,
  removeEntityDocument,
  deleteEntityAssociation,
  deleteEntityAssociations,
  sendAssociationsToWc1,
  getValidityPeriod,
  getCommentHistory,
  getUserFinalizeComment,
  getDataAugmentation,
  getParentsRecords,
  importExternalDocuments,
  getAugmentationSources,
};
